export const MEMBERSHIP_JOURNEY_VARIANTS = [
  'guided_paywall_v1',
  'direct_context_payment_v1'
] as const

export type MembershipJourneyVariant =
  (typeof MEMBERSHIP_JOURNEY_VARIANTS)[number]

const MEMBERSHIP_JOURNEY_BUCKET_COUNT = 10
const GUIDED_PAYWALL_BUCKET_COUNT = 5

const getStableStringBucket = (value: string): number => {
  const hash = Array.from(value).reduce(
    (currentHash, character) =>
      (currentHash * 31 + character.charCodeAt(0)) >>> 0,
    0
  )
  return hash % MEMBERSHIP_JOURNEY_BUCKET_COUNT
}

/**
 * 用团队短 ID 稳定分配会员购买旅程。
 *
 * 数字短 ID 按末位 0-4 / 5-9 做 50:50 分流；非数字 ID 使用稳定哈希。
 * ID 缺失时保守保留会员引导，避免身份信息未加载时直接跳过说明。
 *
 * @param teamShortID - `group.invite_id`，仅缺失时由调用方回退 `groupId`
 * @returns 团队固定命中的会员购买旅程
 */
export const getMembershipJourneyVariant = (
  teamShortID: string
): MembershipJourneyVariant => {
  const normalizedTeamShortID = String(teamShortID || '').trim()
  if (!normalizedTeamShortID) return 'guided_paywall_v1'

  const lastDigitMatch = normalizedTeamShortID.match(/(\d)(?!.*\d)/)
  const bucket = lastDigitMatch
    ? Number(lastDigitMatch[1])
    : getStableStringBucket(normalizedTeamShortID)

  return bucket < GUIDED_PAYWALL_BUCKET_COUNT
    ? 'guided_paywall_v1'
    : 'direct_context_payment_v1'
}

/**
 * 为一次会员卡点生成支付全链路关联标识。
 *
 * @param occurredAt - 卡点发生时间，默认使用当前时间
 * @param entropy - 同毫秒内区分多次卡点的随机值
 * @returns 可写入神策通用字符串字段的短标识
 */
export const createMembershipPaywallAttemptID = (
  occurredAt = Date.now(),
  entropy = Math.random()
): string => {
  const normalizedEntropy = Math.max(0, Math.min(entropy, 0.999999999))
  const entropyToken = Math.floor(normalizedEntropy * 36 ** 6)
    .toString(36)
    .padStart(6, '0')
  return `bwm-${Math.max(0, Math.floor(occurredAt)).toString(36)}-${entropyToken}`
}
